import { Preferences } from '@capacitor/preferences';
import { countsApi } from './api';

/**
 * Counted quantities that survive a bad connection.
 *
 * A storeroom is often the worst-connected place in a building — a metal
 * building full of metal, or a conex with no signal at all — and a blind count
 * is the one job where losing an entry is expensive: the whole point is that
 * the counter wrote down what they actually saw, once, without being told what
 * to expect.
 *
 * Same shape as the photo queue, deliberately: one pattern to understand.
 */

const QUEUE_KEY = 'hxiq:count-queue';

export interface QueuedCount {
  id: string;
  countId: string;
  lineId: string;
  countedQuantity: string;
  attempts: number;
  lastError?: string;
  queuedAt: number;
}

/** What the server says back about a counted line — notably a recount request. */
export interface CountResult {
  recountRequested: boolean;
  message: string | null;
}

export interface CountQueueDeps {
  read: () => Promise<string | null>;
  write: (value: string) => Promise<void>;
  record: (entry: QueuedCount) => Promise<CountResult>;
  isOnline: () => boolean;
}

const realDeps: CountQueueDeps = {
  read: async () => (await Preferences.get({ key: QUEUE_KEY })).value,
  write: async (value) => Preferences.set({ key: QUEUE_KEY, value }),
  record: (entry) =>
    countsApi.record(entry.countId, {
      lineId: entry.lineId,
      countedQuantity: entry.countedQuantity,
    }),
  isOnline: () => navigator.onLine,
};

let deps: CountQueueDeps = realDeps;

/** Test seam. Returns a restore function so a suite cannot leak into the next. */
export function __setCountQueueDeps(next: Partial<CountQueueDeps>): () => void {
  const previous = deps;
  deps = { ...deps, ...next };
  return () => { deps = previous; };
}

type Listener = (queue: QueuedCount[]) => void;
const listeners = new Set<Listener>();
let draining = false;

async function read(): Promise<QueuedCount[]> {
  const value = await deps.read();
  if (!value) return [];
  try {
    return JSON.parse(value) as QueuedCount[];
  } catch {
    // Losing pending entries is bad; refusing to accept more counts is worse.
    return [];
  }
}

async function write(queue: QueuedCount[]): Promise<void> {
  await deps.write(JSON.stringify(queue));
  for (const l of listeners) l(queue);
}

export function onCountQueueChange(listener: Listener): () => void {
  listeners.add(listener);
  void read().then(listener);
  return () => listeners.delete(listener);
}

/**
 * Recording the same line twice replaces the earlier entry rather than adding a
 * second. A recount is a correction, not another observation, and sending both
 * would make the server's variance check argue with itself.
 */
export async function queueCount(
  input: Omit<QueuedCount, 'id' | 'attempts' | 'queuedAt'>,
): Promise<void> {
  const queue = (await read()).filter(
    (q) => !(q.countId === input.countId && q.lineId === input.lineId),
  );
  queue.push({ ...input, id: crypto.randomUUID(), attempts: 0, queuedAt: Date.now() });
  await write(queue);
}

/**
 * Returns what the server said about each line it managed to send.
 *
 * The queue must not swallow that: a variance large enough to need a recount is
 * the single most important thing a counting programme tells anyone, and a
 * count that quietly went through without its warning is worse than one that
 * failed loudly.
 */
export async function drainCounts(): Promise<{ entry: QueuedCount; result: CountResult }[]> {
  const results: { entry: QueuedCount; result: CountResult }[] = [];
  if (draining || !deps.isOnline()) return results;
  draining = true;
  try {
    let queue = await read();
    while (queue.length > 0 && deps.isOnline()) {
      const next = queue[0];
      try {
        const result = await deps.record(next);
        results.push({ entry: next, result });
        await write((await read()).filter((q) => q.id !== next.id));
      } catch (error) {
        queue = await read();
        const target = queue.find((q) => q.id === next.id);
        if (!target) break;
        target.attempts += 1;
        target.lastError = String(error).slice(0, 200);
        // One stuck line must not hold up the rest of the sheet.
        if (target.attempts >= 10) {
          queue = [...queue.filter((q) => q.id !== next.id), target];
        }
        await write(queue);
        break;
      }
      queue = await read();
    }
  } finally {
    draining = false;
  }
  return results;
}

export async function discardCount(id: string): Promise<void> {
  await write((await read()).filter((q) => q.id !== id));
}

export function startCountQueue(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('online', () => void drainCounts());
  void drainCounts();
}
